import { useState } from 'react';
import { ShieldCheck } from 'lucide-react';
import { Button, FormField, Input, Spinner } from '@proerp/ui';
import { AuthShell, ErrorNote } from '@/components/AuthShell';
import { portalRequest } from '@/lib/apiClient';
import { validateNewPassword } from '@/lib/passwordRules';

/**
 * Erzwungener Passwortwechsel beim ersten Login (ADR 0085).
 *
 * Das Anfangspasswort hat eine Administratorin vergeben und per Telefon oder
 * Mail übermittelt — es ist damit mindestens einer weiteren Person bekannt und
 * darf nicht das Dauerpasswort bleiben. Bis der Wechsel erfolgt ist, gibt der
 * `PortalGate` das Portal nicht frei.
 *
 * Der Wechsel beendet serverseitig ALLE Sitzungen (auch die gerade laufende).
 * Deshalb führt `onDone` zurück zur Anmeldung statt ins Portal — mit dem alten
 * Token käme sonst nur ein 401 zurück.
 */
export function ForcePasswordChange({ onDone }: { onDone: () => void }) {
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [repeated, setRepeated] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [isPending, setIsPending] = useState(false);

  async function submit(event: React.FormEvent): Promise<void> {
    event.preventDefault();
    const problem = validateNewPassword({ currentPassword, newPassword, repeated });
    if (problem) {
      setError(problem);
      return;
    }
    setError(null);
    setIsPending(true);
    try {
      await portalRequest<void>('/auth/change-password', {
        method: 'POST',
        body: JSON.stringify({ currentPassword, newPassword }),
      });
      onDone();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Das Passwort konnte nicht geändert werden.');
      setIsPending(false);
    }
  }

  return (
    <AuthShell
      title="Neues Passwort festlegen"
      subtitle="Dein Zugang wurde mit einem Anfangspasswort angelegt. Bitte ersetze es durch ein eigenes."
    >
      <form onSubmit={submit} className="flex flex-col gap-4">
        <FormField label="Anfangspasswort" htmlFor="portalCurrentPassword" required>
          <Input
            id="portalCurrentPassword"
            type="password"
            autoComplete="current-password"
            value={currentPassword}
            onChange={(event) => setCurrentPassword(event.target.value)}
            autoFocus
            required
          />
        </FormField>
        <FormField
          label="Neues Passwort"
          htmlFor="portalNewPassword"
          hint="Mindestens 10 Zeichen."
          required
        >
          <Input
            id="portalNewPassword"
            type="password"
            autoComplete="new-password"
            value={newPassword}
            onChange={(event) => setNewPassword(event.target.value)}
            required
          />
        </FormField>
        <FormField label="Neues Passwort wiederholen" htmlFor="portalRepeatPassword" required>
          <Input
            id="portalRepeatPassword"
            type="password"
            autoComplete="new-password"
            value={repeated}
            onChange={(event) => setRepeated(event.target.value)}
            required
          />
        </FormField>
        <ErrorNote message={error} />
        <p className="text-xs text-muted-foreground">
          Nach dem Speichern meldest du dich einmal mit dem neuen Passwort an.
        </p>
        {/* h-11 ≈ 44 px — sicher treffbar auch auf dem Telefon. */}
        <Button type="submit" variant="primary" disabled={isPending} className="h-11 w-full">
          {isPending ? <Spinner aria-hidden /> : <ShieldCheck className="size-4" aria-hidden />}
          Passwort speichern
        </Button>
      </form>
    </AuthShell>
  );
}
